/* CGG behavior layered on Turbo 11. Keep vendor variant/cart code in Turbo. */
(() => {
  // A blank placement is shown like the app's required options: red label and
  // "required" message, with Add to Cart left as is.
  function markPlacementMissing(picker, select) {
    const field = select.closest('fieldset') || select.parentElement;
    field.classList.add('cgg-placement-error');
    select.setAttribute('aria-invalid', 'true');
    let message = field.querySelector('.cgg-placement-error__message');
    if (!message) {
      message = document.createElement('span');
      message.className = 'cgg-placement-error__message';
      message.id = 'cgg-placement-error-' + (select.id || Math.random().toString(36).slice(2));
      message.textContent = picker.dataset.requiredMessage || 'This option is required.';
      field.append(message);
    }
    select.setAttribute('aria-describedby', message.id);
    // No scroll here: revealFirstMissing brings the first missing field into view once.
  }

  function clearPlacementMissing(select) {
    const field = select.closest('.cgg-placement-error');
    select.removeAttribute('aria-invalid');
    select.removeAttribute('aria-describedby');
    field?.querySelector('.cgg-placement-error__message')?.remove();
    field?.classList.remove('cgg-placement-error');
  }

  function guardPlacement(event) {
    const form = event.target.closest('form');
    const picker = form?.querySelector('kit-variant-picker[data-required-placement]');
    if (!picker) return;
    if (event.type === 'click' && !event.target.closest('.add_to_cart, [name="add"], .shopify-payment-button')) return;
    const missing = [...picker.querySelectorAll('select[data-required-placement]')].filter(select => !select.value);
    if (missing.length || picker.selectionIncomplete || picker.classList.contains('kit-picker--loading') || !picker.querySelector('[name="id"][data-variants]')?.value) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!missing.length) return;
      // The app never sees this click, so show its own required errors too
      // (per option set: no app scroll), then the placement error.
      const optionProduct = window.BOLD?.options?.app?.getOptionProductByForm?.(form);
      optionProduct?.optionSets?.forEach(set => [set.validateOptions?.()].flat(Infinity).forEach(result => result?.catch?.(() => {})));
      missing.forEach(select => markPlacementMissing(picker, select));
    }
  }
  document.addEventListener('click', guardPlacement, true);
  document.addEventListener('submit', guardPlacement, true);
  document.addEventListener('change', event => {
    const select = event.target.closest?.('select[data-required-placement]');
    if (select?.value) clearPlacementMissing(select);
  });
  const initialized = new WeakSet();

  function initializeOptions(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    const drawer = root.querySelector('[data-cgg-options-drawer]');
    const required = root.querySelector('[data-cgg-required-options]');
    const toggle = root.querySelector('[data-cgg-options-toggle]');
    const options = drawer.querySelector('.bold_options');
    const compact = Boolean(root.closest('.cgg-pdp-compact-options'));
    const form = root.closest('[data-product-form]');
    const toggleTitle = compact ? document.createElement('span') : null;
    if (compact) {
      const hint = document.createElement('small');
      hint.textContent = 'Optional';
      toggle.replaceChildren(toggleTitle, hint);
    }
    const summary = compact ? document.createElement('div') : null;
    const paymentTerms = compact ? form?.closest('.product__details--product-page')?.querySelector('[data-payment-terms-target]') : null;
    const paymentNote = paymentTerms ? document.createElement('p') : null;
    if (paymentNote) {
      paymentNote.className = 'cgg-payment-note';
      paymentNote.textContent = 'Excludes extras. Final plans at checkout.';
      paymentNote.hidden = true;
      paymentTerms.after(paymentNote);
    }
    if (summary) {
      summary.className = 'cgg-extras-summary';
      summary.setAttribute('aria-live', 'polite');
      summary.hidden = true;
      toggle.after(summary);
    }

    function syncSummary() {
      if (!summary) return;
      if (paymentNote) paymentNote.hidden = ![...root.querySelectorAll('input:checked')].some(input =>
        !input.disabled && !input.closest('.bold_option_hidden, .cgg-metal-inapplicable') &&
        /[1-9]/.test(input.closest('label')?.querySelector('.bold_option_value_price')?.textContent || ''));
      // Read selections even when the drawer is collapsed; app-hidden groups stay excluded.
      const selected = [...options.querySelectorAll('input:checked')].filter(input =>
        !input.disabled && !/^(No|None)(\b|$)/i.test(input.value) &&
        !input.closest('.bold_option_hidden, .cgg-metal-inapplicable'));
      const labels = selected.map(input => {
        const group = input.closest('.bold_option');
        const name = group?.querySelector('.bold_option_title')?.textContent.trim().replace(/\*$/, '');
        const price = input.closest('label')?.querySelector('.bold_option_value_price')?.textContent.trim();
        const value = input.closest('label')?.querySelector('.bold_option_value_title')?.textContent.trim();
        return { name: [name, /^Yes\b/.test(input.value) ? null : value].filter(Boolean).join(' · '), price: price || 'Free' };
      });
      const open = drawer.classList.contains('cstm-open');
      const title = open ? 'Customizations' : labels.length ? 'Edit customizations' : 'Explore customizations';
      if (toggleTitle.textContent !== title) toggleTitle.textContent = title;
      const signature = JSON.stringify(labels);
      if (summary.dataset.selection !== signature) {
        summary.dataset.selection = signature;
        const heading = document.createElement('strong');
        heading.className = 'cgg-extras-heading';
        heading.textContent = 'Selected extras';
        const rows = labels.map(item => {
          const row = document.createElement('div');
          row.className = 'cgg-extras-row';
          const name = document.createElement('span');
          name.textContent = item.name;
          const cost = document.createElement('strong');
          cost.textContent = /free/i.test(item.price) ? 'Free' : '+' + item.price;
          row.append(name, cost);
          return row;
        });
        const amounts = labels.map(item => /free/i.test(item.price) ? 0 :
          /^\$[\d,]+(?:\.\d{2})?$/.test(item.price) ? Math.round(Number(item.price.replace(/[$,]/g, '')) * 100) : null);
        if (labels.length && amounts.every(amount => amount !== null)) {
          const total = document.createElement('div');
          total.className = 'cgg-extras-row cgg-extras-total';
          const label = document.createElement('span');
          label.textContent = 'Extras total';
          const cost = document.createElement('strong');
          cost.textContent = '+$' + (amounts.reduce((sum, amount) => sum + amount, 0) / 100).toFixed(2);
          total.append(label, cost);
          rows.push(total);
        }
        summary.replaceChildren(heading, ...rows);
      }
      summary.hidden = !labels.length || open;
    }

    function addTextExamples() {
      root.querySelectorAll('.bold_option input[type="text"], .bold_option textarea').forEach(input => {
        const name = input.name.match(/^properties\[(.+)\]$/)?.[1] || '';
        const kind = /^opal colou?r(?:\/s|\(s\)|s)?$/i.test(name) ? 'opal' :
          /^(?:stone|gemstone) colou?r(?:\/s|\(s\)|s)?$/i.test(name) ? 'stone' :
          /^enamel colou?r(?:\/s|\(s\)|s)?$/i.test(name) ? 'enamel' :
          /^enter your letters or numbers$/i.test(name) ? 'letters_numbers' :
          /^enter your letters \(no numbers for this style\)$/i.test(name) ? 'letters_only' : null;
        if (!kind) return;
        const example = root.querySelector('template[data-cgg-text-example="' + kind + '"]');
        if (!example) return;
        if (!input.placeholder) input.placeholder = example.dataset.placeholder;
      });
    }

    function arrangeOptions() {
      // Required sets remain visible, with app-created controls/listeners intact.
      // Scope to this form so quick shop cannot steal another form's inputs.
      options.querySelectorAll('.bold_option_set').forEach(set => {
        const title = set.querySelector('.bold_option_title');
        if (title && (title.textContent.trim().endsWith('*') ||
          (compact && set.querySelector('input[name="properties[Gold Plating]"], input[name="properties[Molding Kit]"]')))) required.appendChild(set);
      });
      if (compact) {
        // Required text/select choices need the same prominence and help as radios.
        root.querySelectorAll('.bold_option').forEach(group => {
          const heading = group.querySelector('.bold_option_title');
          if (!heading) return;
          const name = heading.dataset.cggName || heading.textContent.trim().replace(/\s*(?:\*|· required)$/, '');
          const colorKind = /^Opal Colou?r/i.test(name) ? 'opal' : /^(?:Stone|Gemstone) Colou?r/i.test(name) ? 'stone' : /^Enamel Colou?r/i.test(name) ? 'enamel' : null;
          const isRequired = heading.textContent.trim().endsWith('*') || heading.dataset.cggRequired === 'true' || Boolean(colorKind);
          heading.dataset.cggName = name;
          if (isRequired && !/^(Gold Plating|Gold Color)/.test(name)) {
            heading.dataset.cggRequired = 'true';
            if (!heading.querySelector('[data-cgg-example-trigger]') && heading.textContent !== name) heading.textContent = name;
            const set = group.closest('.bold_option_set');
            if (set && set.parentElement !== required) required.appendChild(set);
            // Keep Bold's validation, property names and values; add native validation
            // only to visible text fields (conditional radios retain app rules).
            group.querySelectorAll('input[type="text"], textarea').forEach(input => {
              if (!group.closest('.bold_option_hidden')) input.required = true;
            });
          }
          const helpName = /^Deep Cut\s*\/\s*Perm Look/i.test(name) ? 'Deep Cut' : name.replace(/^Fang Extensions$/i, 'Fang Extension').replace(/^Tooth Placement$/i, 'Placement');
          const help = colorKind ? root.querySelector('template[data-cgg-color-chart="' + colorKind + '"]') :
            [...(form || root).querySelectorAll('template[data-cgg-option-help]')].find(template => template.dataset.cggOptionHelp.replace(/\s/g, '').toLowerCase() === helpName.replace(/\s/g, '').toLowerCase());
          if (help && !heading.querySelector('[data-cgg-example-trigger]')) {
            const trigger = document.createElement('button');
            trigger.type = 'button';
            trigger.className = 'cgg-example-trigger';
            trigger.setAttribute('data-cgg-example-trigger', '');
            if (colorKind) {
              trigger.classList.add('cgg-chart-trigger');
              trigger.textContent = '(View color chart)';
              heading.classList.add('cgg-chart-heading');
            }
            trigger.setAttribute('aria-label', colorKind ? 'View color chart: ' + name : 'About ' + helpName);
            trigger.setAttribute('aria-haspopup', 'dialog');
            trigger.addEventListener('click', event => {
              event.preventDefault();
              showExample(help, colorKind ? name + ' chart' : helpName, trigger);
            });
            heading.append(trigger);
          }
        });
        root.querySelectorAll('.bold_option_radio').forEach(group => {
          const inputs = [...group.querySelectorAll('input[type="radio"]')];
          if (!inputs.length) return;
          group.classList.add('cgg-option-buttons');
          const groupHeading = group.querySelector('.bold_option_title');
          if (/^Deep Cut\s*\/\s*Perm Look\s*\*?$/i.test(groupHeading?.textContent.trim() || '')) {
            // Shorten the visible heading only; the app's property name stays intact.
            const label = [...groupHeading.childNodes].find(node => node.nodeType === Node.TEXT_NODE);
            if (label) label.textContent = 'Deep Cut';
          }
          const groupName = groupHeading?.textContent.trim().replace(/\*$/, '');
          const help = [...root.querySelectorAll('template[data-cgg-option-help]')]
            .find(template => template.dataset.cggOptionHelp.replace(/\s/g, '').toLowerCase() === groupName?.replace(/\s/g, '').toLowerCase());
          if (help && !group.querySelector('[data-cgg-example-trigger]')) {
            const trigger = document.createElement('button');
            trigger.type = 'button';
            trigger.className = 'cgg-example-trigger';
            trigger.setAttribute('data-cgg-example-trigger', '');
            trigger.setAttribute('aria-label', groupName === 'Fang Extension' ? 'About Fang Extension' : 'View ' + groupName + ' example');
            trigger.setAttribute('aria-haspopup', 'dialog');
            trigger.addEventListener('click', () => showExample(help, groupName, trigger));
            group.querySelector('.bold_option_title').append(trigger);
          }
          const kit = inputs[0].name === 'properties[Molding Kit]';
          if (kit) {
            const heading = group.querySelector('.bold_option_title');
            if (heading && !heading.querySelector('[data-cgg-example-trigger]') && heading.textContent !== 'Molding Kit') heading.textContent = 'Molding Kit';
            inputs.forEach(input => { input.required = true; });
          }
          group.classList.toggle('cgg-option-binary', !kit && inputs.length === 2 &&
            inputs.every(input => /^(Yes|No)(\b|$)/.test(input.value)));
          if (kit) inputs.forEach(input => {
            const title = input.closest('label')?.querySelector('.bold_option_value_title');
            const copy = input.value === 'Yes' ? 'Send me a kit' :
              input.value === 'No (I will send my own molds)' ? 'Use my own molds' : null;
            // Update display text only; Bold's values, prices and listeners stay intact.
            if (title && copy && title.textContent !== copy) title.textContent = copy;
          });
          const metalColors = ['properties[Gold Color]', 'properties[Gold Color (For Gold Plating and Solid Gold Only)]'].includes(inputs[0].name);
          group.classList.toggle('cgg-metal-colors', metalColors);
          if (metalColors) inputs.forEach(input => {
            const copy = { 'Yellow Gold': 'Gold', 'White Gold': 'White', 'Rose Gold': 'Rose' }[input.value];
            const title = input.closest('label')?.querySelector('.bold_option_value_title');
            if (title && copy && title.textContent !== copy) title.textContent = copy;
          });
        });
      }
      if (compact) { syncMetalOptions(); syncSideColors(); }
      // Also explain kits on non-compact forms without changing their controls.
      const kitHeading = root.querySelector('input[name="properties[Molding Kit]"]')?.closest('.bold_option')?.querySelector('.bold_option_title');
      const kitHelp = root.querySelector('template[data-cgg-option-help="Molding Kit"]');
      if (kitHeading && kitHelp && !kitHeading.querySelector('[data-cgg-example-trigger]')) {
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'cgg-example-trigger';
        trigger.setAttribute('data-cgg-example-trigger', '');
        trigger.setAttribute('aria-label', 'About Molding Kit');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.addEventListener('click', event => { event.preventDefault(); showExample(kitHelp, 'Molding Kit', trigger); });
        kitHeading.append(trigger);
      }
      drawer.style.display = options.querySelector('.bold_option_set') ? 'block' : 'none';
      syncSummary();
      calmErrorScroll();
    }

    // Replace only Bold's scroll step: its validation and red labels stay as they are.
    const revealErrors = () => revealSoon(root.closest('form'));
    function calmErrorScroll() {
      const optionProduct = window.BOLD?.options?.app?.getOptionProductByForm?.(root.closest('form'));
      if (optionProduct && optionProduct.scrollToError !== revealErrors) optionProduct.scrollToError = revealErrors;
    }

    let exampleDialog;
    let exampleTrigger;
    function showExample(template, name, trigger, label = name + ' example') {
      if (!exampleDialog) {
        exampleDialog = document.createElement('dialog');
        exampleDialog.className = 'cgg-example-dialog';
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'cgg-example-close';
        close.textContent = '×';
        close.setAttribute('aria-label', 'Close example');
        close.addEventListener('click', () => exampleDialog.close());
        exampleDialog.append(close, document.createElement('div'));
        exampleDialog.addEventListener('click', event => {
          const box = exampleDialog.getBoundingClientRect();
          if (event.target === exampleDialog &&
            (event.clientX < box.left || event.clientX > box.right ||
             event.clientY < box.top || event.clientY > box.bottom)) exampleDialog.close();
        });
        exampleDialog.addEventListener('close', () => exampleTrigger?.focus({ preventScroll: true }));
        root.append(exampleDialog);
      }
      exampleTrigger = trigger;
      exampleDialog.setAttribute('aria-label', label);
      exampleDialog.classList.toggle('cgg-placement-dialog', template.hasAttribute('data-cgg-placement-content'));
      exampleDialog.classList.toggle('cgg-color-chart-dialog', template.hasAttribute('data-cgg-color-chart'));
      exampleDialog.classList.toggle('cgg-comparison-dialog', Boolean(template.content.querySelector('.cgg-style-comparison')));
      const content = exampleDialog.lastElementChild;
      const heading = document.createElement('h3');
      heading.textContent = name;
      content.replaceChildren(heading, template.content.cloneNode(true));
      exampleDialog.showModal();
      initializeChart(content);
      exampleDialog.querySelector('button').focus({ preventScroll: true });
    }

    function initializeChart(content) {
      const viewer = content.querySelector('[data-cgg-chart-viewer]');
      if (!viewer) return;
      const stage = viewer.querySelector('[data-cgg-chart-stage]');
      stage.setAttribute('role', 'region');
      const images = [...stage.querySelectorAll('img')];
      const tabs = viewer.querySelector('[data-cgg-chart-tabs]');
      let active = 0, scale = 1, x = 0, y = 0;
      const points = new Map();
      let previous;
      const draw = () => {
        const image = images[active];
        if (image.naturalWidth && image.naturalHeight) {
          const ratio = image.naturalWidth / image.naturalHeight;
          const width = Math.min(viewer.clientWidth, Math.max(80, window.innerHeight - 320) * ratio);
          stage.style.width = width + 'px';
          stage.style.height = width / ratio + 'px';
        }
        const limitX = stage.clientWidth * (scale - 1) / 2;
        const limitY = stage.clientHeight * (scale - 1) / 2;
        x = Math.max(-limitX, Math.min(limitX, x));
        y = Math.max(-limitY, Math.min(limitY, y));
        images.forEach((img, index) => {
          img.hidden = index !== active;
          img.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
        });
        stage.setAttribute('aria-label', 'Color chart, zoom ' + Math.round(scale * 100) + ' percent');
      };
      const reset = () => { scale = 1; x = 0; y = 0; points.clear(); previous = null; draw(); };
      if (images.length > 1) images.forEach((img, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = 'Chart ' + (index + 1);
        button.setAttribute('aria-pressed', String(index === 0));
        button.addEventListener('click', () => {
          active = index;
          [...tabs.children].forEach((tab, i) => tab.setAttribute('aria-pressed', String(i === index)));
          reset();
        });
        tabs.append(button);
      });
      viewer.querySelector('[data-cgg-zoom="in"]').addEventListener('click', () => { scale = Math.min(4, scale + 0.5); draw(); });
      viewer.querySelector('[data-cgg-zoom="reset"]').addEventListener('click', reset);
      stage.tabIndex = 0;
      stage.addEventListener('keydown', event => {
        if (event.key === '+' || event.key === '=') scale = Math.min(4, scale + 0.5);
        else if (event.key === '-') scale = Math.max(1, scale - 0.5);
        else if (event.key === 'ArrowLeft') x += 30;
        else if (event.key === 'ArrowRight') x -= 30;
        else if (event.key === 'ArrowUp') y += 30;
        else if (event.key === 'ArrowDown') y -= 30;
        else return;
        event.preventDefault(); draw();
      });
      const geometry = () => {
        const p = [...points.values()];
        return p.length > 1 ? { x: (p[0].x + p[1].x) / 2, y: (p[0].y + p[1].y) / 2, distance: Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) } : p[0];
      };
      stage.addEventListener('pointerdown', event => {
        if (event.button !== 0) return;
        stage.setPointerCapture(event.pointerId);
        points.set(event.pointerId, { x: event.clientX, y: event.clientY });
        previous = geometry();
      });
      stage.addEventListener('pointermove', event => {
        if (!points.has(event.pointerId)) return;
        points.set(event.pointerId, { x: event.clientX, y: event.clientY });
        const next = geometry();
        if (previous) {
          if (next.distance && previous.distance) scale = Math.max(1, Math.min(4, scale * next.distance / previous.distance));
          if (scale > 1) { x += next.x - previous.x; y += next.y - previous.y; }
        }
        previous = next; draw();
      });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => stage.addEventListener(type, event => {
        points.delete(event.pointerId); previous = geometry();
      }));
      stage.addEventListener('dragstart', event => event.preventDefault());
      images.forEach(img => img.addEventListener('load', draw));
      reset();
    }

    const sideValues = new WeakMap();
    let syncingSide = false;
    function syncSideColors() {
      if (root.dataset.productHandle !== 'spider-web-enamel-opal-grillz' || syncingSide) return;
      const style = form?.querySelector('select[data-option-name="Style"]')?.value || '';
      const side = /^Top\b/i.test(style) ? 'opal' : /^Bottom\b/i.test(style) ? 'stone' : null;
      if (!side) return;
      syncingSide = true;
      root.querySelectorAll('.bold_option_textbox').forEach(group => {
        let input = group.querySelector('input[type="text"]');
        const kind = group.dataset.cggColorKind || (/^properties\[Opal Color/i.test(input?.name || '') ? 'opal' : /^properties\[Stone Color/i.test(input?.name || '') ? 'stone' : null);
        if (!kind) return;
        group.dataset.cggColorKind = kind;
        const applicable = kind === side;
        // Bold validates its option model, not the input's disabled/required
        // attributes. Use its own visibility methods so the hidden color is
        // also omitted from validation and submitted option properties.
        const optionProduct = window.BOLD?.options?.app?.getOptionProductByForm?.(group.closest('form'));
        const optionModel = optionProduct?.optionSets?.flatMap(set => set.options || []).find(option => option.DOM?.element === group);
        if (!applicable && input?.value) {
          sideValues.set(group, input.value);
          input.value = '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (optionModel) {
          if (applicable) optionModel.show();
          else optionModel.hide();
        }
        group.classList.toggle('cgg-side-inapplicable', !applicable);
        group.classList.toggle('bold_option_hidden', !applicable);
        // Bold detaches the input on hide and restores it on show.
        input = group.querySelector('input[type="text"]') || input;
        if (!input) return;
        input.disabled = !applicable;
        input.required = applicable;
        if (applicable && !input.value && sideValues.has(group)) {
          input.value = sideValues.get(group); sideValues.delete(group);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      syncingSide = false;
    }

    if (compact && form) form.addEventListener('click', event => {
      const trigger = event.target.closest('[data-cgg-placement-help]');
      const template = form.querySelector('template[data-cgg-placement-content]');
        if (trigger && template) showExample(template, 'Tooth placement', trigger, 'How to choose tooth placement');
        const materialTrigger = event.target.closest('[data-cgg-material-help]');
        const materialTemplate = root.querySelector('template[data-cgg-material-content]');
        if (materialTrigger && materialTemplate) showExample(materialTemplate, 'Grillz materials', materialTrigger, 'About grillz materials');
      const barTrigger = event.target.closest('[data-cgg-bar-help]');
      const barTemplate = root.querySelector('template[data-cgg-bar-content]');
      if (barTrigger && barTemplate) showExample(barTemplate, 'Front bar or back bar?', barTrigger, 'About bar types');
    });

    function syncMetalOptions() {
      const barLegend = form?.querySelector('select[data-option-name="Bar Type"]')?.closest('fieldset')?.querySelector('legend');
      if (barLegend && root.querySelector('template[data-cgg-bar-content]') && !barLegend.querySelector('[data-cgg-bar-help]')) {
        const extra = document.createElement('span');
        extra.className = 'kit-text kit-fieldset__legend-extra';
        const help = document.createElement('button');
        help.type = 'button';
        help.className = 'cgg-example-trigger';
        help.dataset.cggBarHelp = '';
        help.setAttribute('aria-label', 'About bar types');
        help.setAttribute('aria-haspopup', 'dialog');
        extra.appendChild(help);
        barLegend.appendChild(extra);
      }
      const material = form?.querySelector('select[data-option-name="Material"], select[data-option-name="Grillz Material"]');
      if (!material) return;
      // Extend the legend without changing the vendor's native v11 picker.
      // The form observer reapplies this after Turbo replaces picker children.
      const legend = material.closest('fieldset')?.querySelector('legend');
      if (legend && root.querySelector('template[data-cgg-material-content]') && !legend.querySelector('[data-cgg-material-help]')) {
        const extra = document.createElement('span');
        extra.className = 'kit-text kit-fieldset__legend-extra';
        const help = document.createElement('button');
        help.type = 'button';
        help.className = 'cgg-example-trigger';
        help.dataset.cggMaterialHelp = '';
        help.setAttribute('aria-label', 'About grillz materials');
        help.setAttribute('aria-haspopup', 'dialog');
        extra.appendChild(help);
        legend.appendChild(extra);
      }
      const silver = /\bsilver\b/i.test(material.value);
      const solidGold = /\bgold\b/i.test(material.value) && !/plated|plating|\bnpg\b|yellow|white|rose/i.test(material.value);
      const plating = root.querySelector('input[name="properties[Gold Plating]"]')?.closest('.bold_option');
      const color = root.querySelector('.cgg-metal-colors');
      if (!silver && !solidGold) return; // Other finishes retain the app's rules.

      const no = plating?.querySelector('input[value="No"]');
      if (plating && no && !no.disabled) {
        // Let Bold remove its paid add-on before hiding the inapplicable control.
        if (solidGold && !no.checked) no.click();
        plating.classList.toggle('cgg-metal-inapplicable', solidGold && no.checked);
        plating.classList.add('cgg-conditional-plating');
        const title = plating.querySelector('.bold_option_title');
          const titleText = title && Array.from(title.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
          if (titleText && titleText.textContent.trim() !== 'Gold Plating') titleText.textContent = 'Gold Plating';
        const platingSet = plating.closest('.bold_option_set');
        if (platingSet && platingSet.parentElement !== required) required.appendChild(platingSet);
      }
      if (color) {
        const plated = Boolean(plating?.querySelector('input[type="radio"]:checked')?.value.match(/^Yes\b/i));
        const applicable = solidGold || (silver && plated);
        // Keep Bold's submitted properties aligned with the visible choice.
        // CSS hiding alone leaves a stale default gold color in the cart.
        const optionProduct = window.BOLD?.options?.app?.getOptionProductByForm?.(color.closest('form'));
        const optionModel = optionProduct?.optionSets?.flatMap(set => set.options || []).find(option => option.DOM?.element === color);
        if (optionModel) {
          if (applicable) optionModel.show();
          else optionModel.hide();
        }
        color.classList.toggle('cgg-metal-inapplicable', !applicable);
        color.classList.add('cgg-conditional-color');
        const colorSet = color.closest('.bold_option_set');
        if (colorSet && colorSet.parentElement !== required) required.appendChild(colorSet);
        color.querySelectorAll('input[type="radio"]').forEach(input => {
          input.required = applicable;
        });
        const colorTitle = color.querySelector('.bold_option_title');
        const label = silver ? 'Plating Color' : 'Gold Color';
        if (colorTitle && colorTitle.textContent !== label) colorTitle.textContent = label;
      }
    }

    toggle.addEventListener('click', () => {
      const open = drawer.classList.toggle('cstm-open');
      toggle.setAttribute('aria-expanded', String(open));
      syncSummary();
    });

    // Apps may load after DOMContentLoaded and re-render on a variant change.
    new MutationObserver(arrangeOptions).observe(options, { childList: true, subtree: true });
    if (compact && form) {
      form.addEventListener('change', syncMetalOptions);
      form.addEventListener('change', syncSideColors);
      form.addEventListener('change', syncSummary);
      // Turbo replaces picker children after variant changes; re-read the current material.
      new MutationObserver(() => { syncMetalOptions(); syncSideColors(); }).observe(form, { childList: true, subtree: true });
    }
    new MutationObserver(addTextExamples).observe(root, { childList: true, subtree: true });
    arrangeOptions();
    addTextExamples();
  }

  // Bold pins its first missing option to the top edge, under the fixed mobile
  // header, even when the option is already on screen. Treat a missing required
  // Placement the same way, and scroll only when the first one is out of view.
  function revealFirstMissing(form) {
    const field = [...(form?.querySelectorAll('.bold_option_error, select[data-required-placement]:invalid') || [])]
      .map(el => el.closest('.bold_option_error, fieldset') || el)
      .find(el => el.getClientRects().length);
    if (!field) return;
    const inset = Math.max(0, ...[...document.querySelectorAll('#header, .sticky_nav')].map(header => {
      const box = header.getBoundingClientRect();
      return /fixed|sticky/.test(getComputedStyle(header).position) && box.top <= 0 ? box.bottom : 0;
    }));
    const box = field.getBoundingClientRect();
    if (box.top >= inset && box.bottom <= window.innerHeight) return;
    window.scrollBy({ top: box.top - inset - 16, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  // Bold and the Add to Cart click can both ask: reveal once, on the next frame,
  // after the click's handlers have marked what is missing.
  const revealing = new WeakSet();
  function revealSoon(form) {
    if (!form || revealing.has(form)) return;
    revealing.add(form);
    requestAnimationFrame(() => { revealing.delete(form); revealFirstMissing(form); });
  }
  // Window capture runs before any handler that stops a blocked click.
  window.addEventListener('click', event => {
    if (event.target.closest?.('.add_to_cart, [name="add"]')) revealSoon(event.target.closest('form'));
  }, true);

  function initializeWithin(node) {
    if (!(node instanceof Element) && node !== document) return;
    if (node.matches?.('[data-cgg-product-options]')) initializeOptions(node);
    node.querySelectorAll('[data-cgg-product-options]').forEach(initializeOptions);
  }

  function start() {
    initializeWithin(document);
    // Section rendering and quick shop can insert additional product forms.
    new MutationObserver(records => {
      for (const record of records) record.addedNodes.forEach(initializeWithin);
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('click', event => {
    if (event.target.closest('[data-cgg-mold-help]')) {
      const details = event.target.closest('.product__details--product-page');
      const heading = [...(details?.querySelectorAll('summary') || [])]
        .find(item => item.textContent.trim() === 'How We Get Your Molds');
      if (heading) {
        heading.closest('details').open = true;
        heading.scrollIntoView({ block: 'center' });
        heading.focus();
      } else window.location.assign('/pages/how-they-work-custom-grillz');
      return;
    }
    const button = event.target.closest('button.read-more');
    if (!button) return;
    const parent = button.closest('.read-more-parent');
    const drawer = parent?.querySelector('.read-more-drawer');
    if (!drawer) return;
    parent.querySelector('.read-more-btn').hidden = true;
    drawer.style.maxHeight = 'none';
    drawer.classList.add('open');
    button.setAttribute('aria-expanded', 'true');
  });

  // Mobile menu: a tap anywhere outside the open <details> closes it (through the
  // theme's own summary handler, so its animation state stays in sync).
  document.addEventListener('click', event => {
    const menu = document.querySelector('details[data-mobile-menu][open]');
    if (!menu || menu.contains(event.target)) return;
    menu.querySelector('[data-mobile-menu-trigger]')?.click();
  });

  document.addEventListener('change', event => {
    if (event.target.matches('[data-cgg-tag-filter]')) window.location.assign(event.target.value);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
